import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, Send, CheckCircle2, Loader2, Wand2, ShieldCheck, XCircle } from 'lucide-react';
import { api } from '@/lib/api';
import { luxe, PILLARS } from '@/lib/luxe';
import { GlassCard, LuxeButton, Input, Textarea, Spinner } from '@/components/bits';

const P = PILLARS.coding;
const STEP_COLORS = {
  completed: luxe.success, running: P.primary, failed: luxe.danger,
  awaiting_approval: luxe.warning, cancelled: luxe.muted, pending: luxe.muted,
};

// Client-only view over the verified Phase 2 backend APIs. No orchestration logic here.
export const MyraBuildChat = ({ projects, onCreateProject }) => {
  const buildProjects = (projects || []).filter((p) => p.id !== 'cambri');
  const [project, setProject] = useState(buildProjects[0]?.id || '');
  const [phase, setPhase] = useState('idle'); // idle|analyzing|clarifying|ready|building
  const [msg, setMsg] = useState('');
  const [req, setReq] = useState(null); // {id, spec, questions}
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState(false);
  const [pipeline, setPipeline] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => { if (!project && buildProjects[0]) setProject(buildProjects[0].id); }, [projects]); // eslint-disable-line
  useEffect(() => () => clearInterval(pollRef.current), []);

  const safeErr = (e) => toast.error((e && e.message) ? e.message : 'Something went wrong, please try again');

  const analyze = async () => {
    if (!project) return toast.error('Create or pick a project first');
    if (msg.trim().length < 3) return toast.error('Describe what you want to build');
    setBusy(true); setPhase('analyzing');
    try {
      const r = await api('/admin/agents/requirements/analyze', { method: 'POST', body: { project, message: msg.trim() } });
      setReq({ id: r.id, spec: r.spec, questions: r.clarifying_questions || [] });
      setPhase(r.ready_to_build ? 'ready' : 'clarifying');
    } catch (e) { safeErr(e); setPhase('idle'); } finally { setBusy(false); }
  };

  const sendAnswer = async () => {
    if (answer.trim().length < 1) return;
    setBusy(true);
    try {
      const r = await api(`/admin/agents/requirements/${req.id}/answer`, { method: 'POST', body: { answers: answer.trim() } });
      setReq({ id: req.id, spec: r.spec, questions: r.clarifying_questions || [] });
      setAnswer('');
      setPhase(r.ready_to_build ? 'ready' : 'clarifying');
    } catch (e) { safeErr(e); } finally { setBusy(false); }
  };

  const pollPipeline = (pid) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const p = await api(`/admin/agents/orchestrator/${pid}`);
        setPipeline(p);
        if (!['running', 'awaiting_approval'].includes(p.status)) clearInterval(pollRef.current);
      } catch { clearInterval(pollRef.current); }
    }, 4000);
  };

  const approveAndBuild = async () => {
    setBusy(true);
    try {
      await api(`/admin/agents/requirements/${req.id}/approve`, { method: 'POST' });
      const b = await api(`/admin/agents/requirements/${req.id}/build`, { method: 'POST' });
      await api(`/admin/agents/orchestrator/${b.pipeline_id}/approve`, { method: 'POST' });
      setPhase('building');
      const p = await api(`/admin/agents/orchestrator/${b.pipeline_id}`);
      setPipeline(p); pollPipeline(b.pipeline_id);
      toast.success('Build started');
    } catch (e) { safeErr(e); } finally { setBusy(false); }
  };

  const cancelBuild = async () => {
    if (!pipeline) return;
    try { await api(`/admin/agents/orchestrator/${pipeline.id}/cancel`, { method: 'POST' }); } catch (e) { safeErr(e); }
  };

  const reset = () => { clearInterval(pollRef.current); setReq(null); setPipeline(null); setMsg(''); setAnswer(''); setPhase('idle'); };

  const s = req?.spec || {};
  const plannedSteps = pipeline?.pipeline || [];
  const runByIndex = Object.fromEntries((pipeline?.steps || []).map((x) => [x.index, x]));

  return (
    <GlassCard className="p-6 mb-8" data-testid="myra-build-chat">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles size={18} style={{ color: P.primary }} />
        <span className="font-bold text-lg" style={{ color: luxe.text }}>Myra Build</span>
        <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: luxe.surface2, color: luxe.muted, border: `1px solid ${luxe.border}` }}>one chat · builds the whole app</span>
      </div>
      <p className="text-sm mb-4" style={{ color: luxe.muted }}>Describe your app in plain language. Myra understands it, asks only what's needed, then builds it for you.</p>

      {/* Project selector */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-xs" style={{ color: luxe.muted }}>Project:</span>
        {buildProjects.length === 0 ? (
          <LuxeButton variant="ghost" color={P} onClick={onCreateProject} data-testid="myra-build-create-project">Create a project first</LuxeButton>
        ) : (
          <select value={project} onChange={(e) => setProject(e.target.value)} data-testid="myra-build-project-select"
            className="text-sm rounded-lg px-3 py-2" style={{ background: luxe.surface2, color: luxe.text, border: `1px solid ${luxe.border}` }}>
            {buildProjects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
      </div>

      {/* Idle / initial input */}
      {phase === 'idle' && (
        <div className="flex gap-2">
          <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={3}
            placeholder="e.g. Build a simple public note-taking app where anyone can add, view and delete notes."
            data-testid="myra-build-input" />
          <LuxeButton variant="pillar" color={P} onClick={analyze} disabled={busy} data-testid="myra-build-analyze-btn">
            <Send size={16} className="inline mr-1" />Analyze
          </LuxeButton>
        </div>
      )}

      {phase === 'analyzing' && (
        <div className="flex items-center gap-2 text-sm" style={{ color: luxe.muted }} data-testid="myra-build-analyzing">
          <Spinner /> Myra is understanding your requirement…
        </div>
      )}

      {/* Clarification panel */}
      {phase === 'clarifying' && req && (
        <div data-testid="myra-build-clarify">
          <div className="text-sm font-bold mb-2" style={{ color: luxe.text }}>A few quick questions to get this right:</div>
          <ul className="space-y-2 mb-3">
            {req.questions.map((q, i) => (
              <li key={i} className="text-sm flex gap-2" style={{ color: luxe.textMid }} data-testid={`myra-build-question-${i}`}>
                <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: luxe.surface2, color: q.priority === 'high' ? luxe.warning : luxe.muted, border: `1px solid ${luxe.border}` }}>{q.priority || 'q'}</span>
                {q.q}
              </li>
            ))}
          </ul>
          <div className="flex gap-2">
            <Textarea value={answer} onChange={(e) => setAnswer(e.target.value)} rows={2} placeholder="Answer in your own words…" data-testid="myra-build-answer-input" />
            <LuxeButton variant="pillar" color={P} onClick={sendAnswer} disabled={busy} data-testid="myra-build-answer-btn"><Send size={16} /></LuxeButton>
          </div>
        </div>
      )}

      {/* Requirement summary + approve */}
      {phase === 'ready' && req && (
        <div data-testid="myra-build-ready">
          <div className="p-4 rounded-xl mb-3" style={{ background: luxe.surface2, border: `1px solid ${luxe.border}` }}>
            <div className="flex items-center gap-2 mb-2"><CheckCircle2 size={16} style={{ color: luxe.success }} /><span className="font-bold" style={{ color: luxe.text }}>Ready to build</span></div>
            <div className="text-sm mb-2" style={{ color: luxe.textMid }}>{s.summary || s.goal}</div>
            {s.features?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {s.features.slice(0, 10).map((f, i) => <span key={i} className="text-xs px-2 py-1 rounded-full" style={{ background: luxe.bgElev, color: luxe.muted, border: `1px solid ${luxe.border}` }}>{f}</span>)}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <LuxeButton variant="pillar" color={P} onClick={approveAndBuild} disabled={busy} data-testid="myra-build-approve-btn">
              <Wand2 size={16} className="inline mr-1" />Approve & Start Build
            </LuxeButton>
            <LuxeButton variant="ghost" color={P} onClick={reset} data-testid="myra-build-reset-btn">Start over</LuxeButton>
          </div>
        </div>
      )}

      {/* Pipeline progress */}
      {phase === 'building' && pipeline && (
        <div data-testid="myra-build-pipeline">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-bold" style={{ color: luxe.text }}>
              Build status: <span style={{ color: STEP_COLORS[pipeline.status] || luxe.text }} data-testid="myra-build-status">{pipeline.status}</span>
            </div>
            {['running', 'awaiting_approval'].includes(pipeline.status)
              ? <button onClick={cancelBuild} className="text-xs flex items-center gap-1" style={{ color: luxe.danger }} data-testid="myra-build-cancel-btn"><XCircle size={13} /> Cancel</button>
              : <button onClick={reset} className="text-xs" style={{ color: luxe.muted }} data-testid="myra-build-new-btn">New build</button>}
          </div>
          <div className="space-y-2">
            {plannedSteps.map((st, i) => {
              const run = runByIndex[i];
              const status = run?.status || (pipeline.current_index === i ? 'running' : (pipeline.current_index > i ? 'completed' : 'pending'));
              return (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg" style={{ background: luxe.surface2, border: `1px solid ${luxe.border}` }} data-testid={`myra-build-step-${i}`}>
                  <div className="text-xs font-bold w-6" style={{ color: luxe.muted }}>{i + 1}</div>
                  <div className="flex-1">
                    <div className="text-sm font-bold capitalize" style={{ color: luxe.text }}>{st.agent.replace(/_/g, ' ')}</div>
                    {st.required_approval && <div className="text-[10px] flex items-center gap-1" style={{ color: luxe.warning }}><ShieldCheck size={11} /> approval-gated</div>}
                  </div>
                  <div className="text-xs font-bold flex items-center gap-1" style={{ color: STEP_COLORS[status] || luxe.muted }}>
                    {status === 'running' && <Loader2 size={13} className="animate-spin" />}
                    {status}
                  </div>
                </div>
              );
            })}
          </div>
          {pipeline.final?.summary && (
            <div className="mt-3 text-xs p-3 rounded-lg" style={{ background: luxe.bgElev, color: luxe.muted, border: `1px solid ${luxe.border}` }} data-testid="myra-build-final">{pipeline.final.summary}</div>
          )}
        </div>
      )}
    </GlassCard>
  );
};
